/**
 * Clickable match score — opens the "Why did I get this match?" explainer.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';

export interface MatchScoreButtonProps {
  score: number;
  onClick?: () => void;
  /** compact = table chip; ring = circular gauge style */
  variant?: 'chip' | 'ring';
  className?: string;
}

function scoreColor(score: number): { fg: string; bg: string; ring: string } {
  if (score >= 75) return { fg: 'var(--ds-ok)', bg: 'rgba(34,197,94,0.1)', ring: 'rgba(34,197,94,0.35)' };
  if (score >= 55) return { fg: 'var(--ds-warn)', bg: 'rgba(245,158,11,0.1)', ring: 'rgba(245,158,11,0.35)' };
  if (score >= 35) return { fg: 'var(--ds-brand)', bg: 'rgba(108,71,255,0.08)', ring: 'rgba(108,71,255,0.25)' };
  return { fg: 'var(--ds-text-muted)', bg: 'var(--ds-bg-recessed)', ring: 'var(--ds-border)' };
}

const MatchScoreButton: React.FC<MatchScoreButtonProps> = ({
  score,
  onClick,
  variant = 'chip',
  className = '',
}) => {
  const { t } = useTranslation();
  const pct = Math.max(0, Math.min(100, Math.round(score)));
  const colors = scoreColor(pct);

  if (!onClick || pct < 1) {
    return variant === 'chip' ? (
      <span className={`ds-chip t-card-title t-num ${className}`} style={{ color: colors.fg }}>
        {pct}%
      </span>
    ) : (
      <span className={`ds-match-score-btn ds-match-score-btn--static ${className}`}>
        <span className="ds-match-score-btn__value t-num">{pct}%</span>
      </span>
    );
  }

  const label = t('dashboard.matchExplain.clickWhy', {
    score: pct,
    defaultValue: `Match: ${pct}% — click to see why`,
  });

  if (variant === 'ring') {
    return (
      <button
        type="button"
        className={`ds-match-score-btn ds-match-score-btn--ring ${className}`}
        onClick={(e) => { e.stopPropagation(); onClick(); }}
        aria-label={label}
      >
        <span className="ds-match-score-btn__value t-num" style={{ color: colors.fg }}>{pct}%</span>
        <span className="ds-match-score-btn__hint">{t('dashboard.matchExplain.whyShort', 'Why?')}</span>
      </button>
    );
  }

  return (
    <button
      type="button"
      className={`ds-match-score-btn ds-match-score-btn--chip ${className}`}
      style={{ background: colors.bg, color: colors.fg, borderColor: colors.ring }}
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      aria-label={label}
    >
      <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: colors.fg }} aria-hidden />
      <span className="ds-match-score-btn__value t-num">{pct}%</span>
      <span className="ds-match-score-btn__hint">{t('dashboard.matchExplain.whyShort', 'Why?')}</span>
    </button>
  );
};

export default React.memo(MatchScoreButton);
